// features/procurement/services/procurementApi.ts
/**
 * Procurement API Service — Centralized API calls for all procurement operations.
 */

import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface Vendor {
    id: string;
    name: string;
    contactPerson: string | null;
    email: string | null;
    phone: string | null;
    alternatePhone: string | null;
    website: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    country: string;
    taxId: string | null;
    registrationNumber: string | null;
    paymentTerms: string | null;
    creditLimit: string | null;
    currency: string;
    bankName: string | null;
    bankAccountNumber: string | null;
    bankAccountName: string | null;
    bankSortCode: string | null;
    category: string | null;
    leadTimeDays: number;
    minimumOrderAmount: string | null;
    accountNumber: string | null;
    rating: number;
    isPreferred: boolean;
    isActive: boolean;
    notes: string | null;
    tags: string | null;
    createdAt: string;
    _count?: { purchaseOrders: number };
    totalPurchases?: string;
    lastOrderDate?: string;
}

export interface PurchaseRequisition {
    id: string;
    tenantId: string;
    branchId: string;
    branchName?: string;
    requisitionNumber: string;
    requestedBy: string;
    requesterName?: string;
    status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'converted_to_po';
    items: PurchaseRequisitionItem[];
    notes: string | null;
    approvedBy: string | null;
    approvedAt: string | null;
    rejectedReason: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface PurchaseRequisitionItem {
    id: string;
    requisitionId: string;
    productId: string;
    productName?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
    estimatedUnitCost: string | null;
    notes: string | null;
}

export interface PurchaseOrder {
    id: string;
    tenantId: string;
    branchId: string;
    branchName?: string;
    vendorId: string;
    vendorName?: string;
    poNumber: string;
    requisitionId: string | null;
    status: 'draft' | 'pending_approval' | 'approved' | 'sent' | 'partially_received' | 'fully_received' | 'cancelled';
    expectedDeliveryDate: string | null;
    deliveryInstructions: string | null;
    paymentTerms: string | null;
    subtotal: string;
    taxAmount: string;
    totalAmount: string;
    items: PurchaseOrderItem[];
    notes: string | null;
    createdBy: string;
    createdByName?: string;
    approvedBy: string | null;
    approvedAt: string | null;
    sentAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface PurchaseOrderItem {
    id: string;
    poId: string;
    productId: string;
    productName?: string;
    quantityOrdered: string;
    quantityReceived: string;
    uomId: string;
    uomName?: string;
    unitCost: string;
    totalCost: string;
    taxRate: string;
    notes: string | null;
}

export interface GoodsReceivedNote {
    id: string;
    tenantId: string;
    branchId: string;
    branchName?: string;
    poId: string | null;
    poNumber?: string;
    vendorId: string;
    vendorName?: string;
    grnNumber: string;
    deliveryNoteNumber: string | null;
    invoiceNumber: string | null;
    receivedBy: string;
    receiverName?: string;
    status: 'draft' | 'confirmed' | 'disputed';
    items: GoodsReceivedItem[];
    notes: string | null;
    receivedAt: string;
    createdAt: string;
    updatedAt: string;
}

export interface GoodsReceivedItem {
    id: string;
    grnId: string;
    poItemId: string | null;
    productId: string;
    productName?: string;
    quantityOrdered: string;
    quantityReceived: string;
    quantityAccepted: string;
    quantityRejected: string;
    uomId: string;
    uomName?: string;
    unitCost: string;
    batchNumber: string | null;
    expiryDate: string | null;
    rejectionReason: string | null;
    notes: string | null;
}

export interface PurchaseReturn {
    id: string;
    tenantId: string;
    branchId: string;
    branchName?: string;
    vendorId: string;
    vendorName?: string;
    grnId: string | null;
    returnNumber: string;
    status: 'draft' | 'approved' | 'shipped' | 'received_by_vendor' | 'credit_note_received';
    items: PurchaseReturnItem[];
    totalAmount?: string;
    reason: string | null;
    createdBy: string;
    createdByName?: string;
    approvedBy: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface PurchaseReturnItem {
    id: string;
    returnId: string;
    productId: string;
    productName?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
    unitCost: string;
    reason: string | null;
}

// ============================================================
// FRIENDLY ERROR MESSAGES
// ============================================================

export function getProcurementErrorMessage(err: any, fallback: string = 'Something went wrong. Please try again.'): string {
    const message = err?.response?.data?.error?.message || err?.message || '';

    // Map backend error codes to user-friendly messages
    if (message.includes('VENDOR_NOT_FOUND')) return 'Vendor not found. Please select a valid vendor.';
    if (message.includes('PO_CANNOT_APPROVE')) return 'This purchase order cannot be approved in its current status.';
    if (message.includes('PO_NOT_APPROVED')) return 'This purchase order must be approved before it can be sent.';
    if (message.includes('GRN_ALREADY_CONFIRMED')) return 'This GRN has already been confirmed.';
    if (message.includes('REQ_CANNOT_APPROVE')) return 'This requisition cannot be approved in its current status.';
    if (message.includes('REQ_NOT_APPROVED')) return 'This requisition must be approved before converting to PO.';
    if (message.includes('RETURN_NOT_DRAFT')) return 'Only draft returns can be approved.';
    if (message.includes('SAME_BRANCH')) return 'Source and destination branches must be different.';
    if (message.includes('INSUFFICIENT_STOCK')) return 'Not enough stock available for this operation.';
    if (message.includes('INVALID_BRANCH')) return 'Invalid branch selected. Please check your branch settings.';
    if (message.includes('NO_BRANCH_FOUND')) return 'No branch found. Please create a branch first.';
    if (message.includes('INVALID_FILE')) return 'Invalid file format. Please upload a valid Excel file.';
    if (message.includes('EMPTY_FILE')) return 'The file is empty or has no data rows.';

    // If we have a message, clean it up
    if (message) return message.replace(/^Error:\s*/, '').trim();
    return fallback;
}

// ============================================================
// VENDORS
// ============================================================

export async function getVendors(tenantId: string, params?: { search?: string; category?: string; isActive?: boolean; limit?: number }): Promise<Vendor[]> {
    const clampedParams = params
        ? { ...params, limit: Math.min(params.limit || 100, 200) }
        : undefined;
    const { data } = await apiClient.get(`/tenants/${tenantId}/vendors`, { params: clampedParams });
    const payload = data?.data !== undefined ? data.data : data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.vendors)) return payload.vendors;
    return [];
}

export async function getVendor(tenantId: string, vendorId: string): Promise<Vendor> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/vendors/${vendorId}`);
    return data?.data || data;
}

export async function createVendor(tenantId: string, payload: Partial<Vendor>): Promise<Vendor> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/vendors`, payload);
    return data?.data || data;
}

export async function updateVendor(tenantId: string, vendorId: string, payload: Partial<Vendor>): Promise<Vendor> {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/vendors/${vendorId}`, payload);
    return data?.data || data;
}

export async function deleteVendor(tenantId: string, vendorId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/vendors/${vendorId}`);
}

// ============================================================
// PURCHASE REQUISITIONS
// ============================================================

export async function getRequisitions(tenantId: string, params?: { limit?: number; status?: string }): Promise<{ requisitions: PurchaseRequisition[]; totalCount: number }> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/purchase-requisitions`, { params });
    const payload = data?.data !== undefined ? data.data : data;
    if (Array.isArray(payload)) return { requisitions: payload, totalCount: payload.length };
    return { requisitions: payload?.requisitions || [], totalCount: payload?.totalCount || (payload?.requisitions?.length || 0) };
}

export async function getRequisition(tenantId: string, reqId: string): Promise<PurchaseRequisition> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/purchase-requisitions/${reqId}`);
    return data?.data || data;
}

export async function createRequisition(tenantId: string, payload: { branchId: string; notes?: string; items: Array<{ productId: string; quantity: string; uomId: string; estimatedUnitCost?: string; notes?: string }> }): Promise<PurchaseRequisition> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-requisitions`, payload);
    return data?.data || data;
}

export async function approveRequisition(tenantId: string, reqId: string): Promise<PurchaseRequisition> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-requisitions/${reqId}/approve`);
    return data?.data || data;
}

export async function rejectRequisition(tenantId: string, reqId: string, reason: string): Promise<PurchaseRequisition> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-requisitions/${reqId}/reject`, { reason });
    return data?.data || data;
}

export async function convertRequisitionToPO(tenantId: string, reqId: string, vendorId: string, paymentTerms?: string, expectedDeliveryDate?: string): Promise<PurchaseOrder> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-requisitions/${reqId}/convert-to-po`, { vendorId, paymentTerms, expectedDeliveryDate });
    return data?.data || data;
}

export async function autoGenerateRequisitions(tenantId: string, branchId?: string): Promise<{ requisitionsCreated: number; productsRequested: number; skippedCount: number }> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-requisitions/auto`, null, { params: { branchId } });
    return data?.data || data;
}

export const autoRequisition = autoGenerateRequisitions;

// ============================================================
// PURCHASE ORDERS
// ============================================================

export async function getPurchaseOrders(tenantId: string, params?: { limit?: number; status?: string; vendorId?: string }): Promise<{ pos: PurchaseOrder[]; totalCount: number }> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/purchase-orders`, { params });
    const payload = data?.data !== undefined ? data.data : data;
    if (Array.isArray(payload)) return { pos: payload, totalCount: payload.length };
    return { pos: payload?.pos || [], totalCount: payload?.totalCount || (payload?.pos?.length || 0) };
}

export async function getPurchaseOrder(tenantId: string, poId: string): Promise<PurchaseOrder> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/purchase-orders/${poId}`);
    return data?.data || data;
}

export async function createPurchaseOrder(tenantId: string, payload: { branchId: string; vendorId: string; requisitionId?: string; expectedDeliveryDate?: string; deliveryInstructions?: string; paymentTerms?: string; notes?: string; items: Array<{ productId: string; quantityOrdered: string; uomId: string; unitCost: string; taxRate?: string }> }): Promise<PurchaseOrder> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-orders`, payload);
    return data?.data || data;
}

export async function approvePurchaseOrder(tenantId: string, poId: string): Promise<PurchaseOrder> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-orders/${poId}/approve`);
    return data?.data || data;
}

export async function sendPurchaseOrder(tenantId: string, poId: string): Promise<PurchaseOrder> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-orders/${poId}/send`);
    return data?.data || data;
}

export function getPoPdfUrl(tenantId: string, poId: string): string {
    const baseURL = apiClient.defaults.baseURL || '/api/v1';
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') || '' : '';
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${baseURL}/tenants/${tenantId}/purchase-orders/${poId}/pdf${tokenParam}`;
}

export async function downloadPoPdf(tenantId: string, poId: string, poNumber?: string): Promise<void> {
    try {
        const response = await apiClient.get(`/tenants/${tenantId}/purchase-orders/${poId}/pdf`, {
            responseType: 'blob',
        });
        const filename = `${poNumber || 'PurchaseOrder'}_${new Date().toISOString().slice(0, 10)}.pdf`;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    } catch {
        window.open(getPoPdfUrl(tenantId, poId), '_blank');
    }
}

// ============================================================
// GRN
// ============================================================

export async function getGRNs(tenantId: string, params?: { limit?: number; status?: string; vendorId?: string }): Promise<GoodsReceivedNote[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/goods-received`, { params });
    const payload = data?.data !== undefined ? data.data : data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.grns)) return payload.grns;
    return [];
}

export async function getGRN(tenantId: string, grnId: string): Promise<GoodsReceivedNote> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/goods-received/${grnId}`);
    return data?.data || data;
}

export function getGrnPdfUrl(tenantId: string, grnId: string): string {
    const baseURL = apiClient.defaults.baseURL || '/api/v1';
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') || '' : '';
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${baseURL}/tenants/${tenantId}/goods-received/${grnId}/pdf${tokenParam}`;
}

export async function downloadGrnPdf(tenantId: string, grnId: string, grnNumber?: string): Promise<void> {
    try {
        const response = await apiClient.get(`/tenants/${tenantId}/goods-received/${grnId}/pdf`, {
            responseType: 'blob',
        });
        const filename = `${grnNumber || 'GRN'}_${new Date().toISOString().slice(0, 10)}.pdf`;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    } catch {
        window.open(getGrnPdfUrl(tenantId, grnId), '_blank');
    }
}

export async function createGRN(tenantId: string, payload: {
    branchId: string;
    poId?: string;
    vendorId: string;
    deliveryNoteNumber?: string;
    invoiceNumber?: string;
    notes?: string;
    updateSellingPrices?: boolean;
    items: Array<{
        poItemId?: string;
        productId: string;
        quantityOrdered?: string;
        quantityReceived: string;
        quantityRejected?: string;
        uomId: string;
        unitCost: string;
        sellingPrice?: string;
        batchNumber?: string;
        expiryDate?: string;
        rejectionReason?: string;
    }>;
}): Promise<GoodsReceivedNote> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/goods-received`, payload);
    return data?.data || data;
}

export async function confirmGRN(tenantId: string, grnId: string): Promise<GoodsReceivedNote> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/goods-received/${grnId}/confirm`);
    return data?.data || data;
}

// ============================================================
// PURCHASE RETURNS
// ============================================================

export async function getReturns(tenantId: string, params?: { limit?: number; status?: string; vendorId?: string }): Promise<PurchaseReturn[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/purchase-returns`, { params });
    const payload = data?.data !== undefined ? data.data : data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.returns)) return payload.returns;
    return [];
}

export async function getReturn(tenantId: string, returnId: string): Promise<PurchaseReturn> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/purchase-returns/${returnId}`);
    return data?.data || data;
}

export async function createReturn(tenantId: string, payload: { branchId: string; vendorId: string; grnId?: string; reason?: string; items: Array<{ productId: string; quantity: string; uomId: string; unitCost: string; reason?: string }> }): Promise<PurchaseReturn> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-returns`, payload);
    return data?.data || data;
}

export async function approveReturn(tenantId: string, returnId: string): Promise<PurchaseReturn> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/purchase-returns/${returnId}/approve`);
    return data?.data || data;
}

// ============================================================
// LOW STOCK
// ============================================================

export async function checkLowStock(tenantId: string, branchId?: string): Promise<{ lowStockCount: number; items: Array<{ productId: string; productName?: string; currentStock: string; threshold: number }> }> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/inventory/low-stock`, { params: { branchId } });
    return data?.data || { lowStockCount: 0, items: [] };
}

// ============================================================
// VENDOR BULK UPLOAD
// ============================================================

export interface VendorBulkUploadResult {
    totalRows: number;
    successCount: number;
    errorCount: number;
    skippedCount: number;
    errors: Array<{
        row: number;
        vendorName: string;
        field: string;
        message: string;
        suggestedFix?: string;
    }>;
    hasErrors: boolean;
}

export async function downloadVendorTemplate(tenantId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/vendors/bulk-template`, {
        responseType: 'blob',
    });
    return response.data;
}

export async function bulkUploadVendors(tenantId: string, file: File): Promise<VendorBulkUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/tenants/${tenantId}/vendors/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
}